import React, { useState } from "react";
import CountryDropdown from "../../Forms/CountryDropdown";

/**
 * Config for every table key that can appear in data.tables.
 * Columns lists all possible column keys; only columns that have
 * at least one non-empty value in the rows are rendered.
 */
const COUNTRY_COLUMN_KEYS = new Set([
  "tax_residence",
  "country",
  "country_of_birth",
  "jurisdiction",
  "jurisdiction_of_residence",
  "jurisdiction_of_tax_residence",
]);

const TABLE_CONFIG = {
  // Self-Cert Individual
  tax_purposes: {
    label: "Additional Tax Jurisdictions",
    columns: [
      { key: "tax_residence", header: "Jurisdiction of Tax Residence", type: "country" },
      { key: "tax_id", header: "Tax Identification Number" },
      { key: "tax_reference_number_type", header: "Tax Reference Number Type" },
      { key: "reason_for_non_tin", header: "Reason for non-availability of TIN" },
    ],
  },
  // Self-Cert Entity – Section 2
  section_2_tax_purposes: {
    label: "Tax Jurisdictions (Section 2)",
    columns: [
      { key: "tax_residence", header: "Jurisdiction of Tax Residence", type: "country" },
      { key: "tax_id", header: "Tax Identification Number" },
      { key: "tax_reference_number", header: "Tax Reference Number Type" },
      { key: "reason_for_non_tin", header: "Reason for non-availability of TIN" },
    ],
  },
  // Self-Cert Entity – Section 3 controlling persons list
  section_3_controlling_persons: {
    label: "Controlling Persons (Section 3)",
    columns: [
      { key: "name", header: "Name" },
      { key: "dob", header: "Date of Birth" },
      { key: "country_of_birth", header: "Country of Birth", type: "country" },
      { key: "city_of_birth", header: "City / Town of Birth" },
      { key: "address", header: "Address" },
      { key: "country", header: "Country", type: "country" },
    ],
  },
  // Self-Cert Entity – Section 4 controlling person tax jurisdictions
  section_4_tax_purposes: {
    label: "Controlling Person Tax Jurisdictions (Section 4)",
    columns: [
      { key: "tax_residence", header: "Jurisdiction of Tax Residence", type: "country" },
      { key: "tax_id", header: "Tax Identification Number" },
      { key: "tax_reference_number", header: "Tax Reference Number Type" },
      { key: "reason_for_non_tin", header: "Reason for non-availability of TIN" },
    ],
  },
};

const isCountryColumn = (col) => col.type === "country" || COUNTRY_COLUMN_KEYS.has(col.key);

const hasValue = (v) => v !== null && v !== undefined && v !== "";

const thStyle = {
  border: "1px solid #ddd",
  padding: "8px 10px",
  textAlign: "left",
  fontWeight: "600",
  color: "#3B3B3B",
  whiteSpace: "nowrap",
  backgroundColor: "#f5f5f5",
};

const tdStyle = {
  border: "1px solid #ddd",
  padding: "7px 10px",
  fontSize: "13px",
  verticalAlign: "top",
};

/**
 * Renders any array tables stored in data.tables.
 * Works for both SingleFormPopup and DataValidationPopup.
 *
 * Props:
 *   data        – the extracted form data object (must have a .tables property)
 *   fontFamily  – font family string to apply
 */
const FormTablesDisplay = ({ data, fontFamily = "inherit" }) => {
  const [, setRevision] = useState(0);

  if (!data?.tables || typeof data.tables !== "object") return null;

  const tableKeys = Object.keys(TABLE_CONFIG).filter((key) => {
    const rows = data.tables[key];
    return (
      Array.isArray(rows) && rows.length > 0 && rows.some((r) => Object.values(r).some(hasValue))
    );
  });

  if (tableKeys.length === 0) return null;

  const handleCountryChange = (row, colKey, selectedValue) => {
    if (!selectedValue || selectedValue === "OTHER_CUSTOM") return;
    row[colKey] = selectedValue;
    setRevision((n) => n + 1);
  };

  return (
    <>
      {tableKeys.map((tableKey) => {
        const config = TABLE_CONFIG[tableKey];
        const rows = data.tables[tableKey].filter((r) => Object.values(r).some(hasValue));

        // Only show columns that have at least one non-empty value
        const activeColumns = config.columns.filter((col) =>
          rows.some((r) => hasValue(r[col.key]))
        );

        return (
          <div key={tableKey} style={{ marginTop: "20px" }}>
            {/* Section heading */}
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#3B3B3B",
                fontFamily,
                padding: "6px 0 8px",
                borderBottom: "2px solid #EEEDED",
                marginBottom: "8px",
              }}
            >
              {config.label}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                  fontFamily,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: "36px", textAlign: "center" }}>#</th>
                    {activeColumns.map((col) => (
                      <th key={col.key} style={thStyle}>
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: "center",
                          color: "#888",
                          fontWeight: "600",
                        }}
                      >
                        {idx + 1}
                      </td>
                      {activeColumns.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            ...tdStyle,
                            color: hasValue(row[col.key])
                              ? "var(--application-primary-color)"
                              : "#bbb",
                          }}
                        >
                          {isCountryColumn(col) ? (
                            <CountryDropdown
                              label=""
                              hideLabel
                              compact
                              id={`${tableKey}-${col.key}-${idx}`}
                              name={`${tableKey}-${col.key}-${idx}`}
                              value={hasValue(row[col.key]) ? String(row[col.key]) : ""}
                              onChange={(e) => handleCountryChange(row, col.key, e.target.value)}
                            />
                          ) : hasValue(row[col.key]) ? (
                            row[col.key]
                          ) : (
                            "—"
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default FormTablesDisplay;
